import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import api from "../../API/axios";
import { useSocialAuth } from "../../hooks/useSocialAuth";

declare global {
  interface Window {
    FB: any;
    google: any;
    fbAsyncInit: any;
    AppleID: any;
  }
}

interface LoginProps {
  config: any | null;
  onAuthSuccess: (authData: any) => void;
  onStateChange: (state: "login" | "register" | "forgotPassword" | "setPassword" | "twoFactorSetup") => void;
  getConsumerToken?: () => string | null;
  setConsumerToken?: (token: string) => void;
  makeConsumerRequest?: (url: string, options?: RequestInit) => Promise<Response>;
  showSnackbar?: (message: string, severity: "success" | "error" | "warning" | "info") => void;
}

const Login: React.FC<LoginProps> = ({
  config ="234070932893-4ba483jeo3s02ru71dledn9i48ld75ea.apps.googleusercontent.com",
  onAuthSuccess,
  onStateChange,
  setConsumerToken,
  makeConsumerRequest,
  showSnackbar,
}) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [googleSdkLoaded, setGoogleSdkLoaded] = useState(false);
  const googleBtnRef = useRef<HTMLDivElement>(null);

  // Use shared social auth hook with login mode
  const {
    socialLoading,
    error: socialError,
    handleGoogleLogin: handleGoogleLoginBase,
    handleAppleLogin: handleAppleLoginBase,
    handleFacebookLogin: handleFacebookLoginBase,
  } = useSocialAuth({
    clientId: config?.clientId,
    onAuthSuccess,
    setConsumerToken,
    showSnackbar,
  });

  // Wrap handlers to pass login mode
  const handleGoogleLogin = () => handleGoogleLoginBase({ mode: "login" });
  const handleAppleLogin = () => handleAppleLoginBase({ mode: "login" });
  const handleFacebookLogin = () => handleFacebookLoginBase({ mode: "login" });

  // Facebook SDK integration
  useEffect(() => {
    if (!window.FB) {
      window.fbAsyncInit = function () {
        window.FB.init({
          appId: config?.facebookAppId || "4196547247300935",
          cookie: true,
          xfbml: true,
          version: "v19.0",
        });
      };
      const script = document.createElement("script");
      script.src = "https://connect.facebook.net/en_US/sdk.js";
      script.async = true;
      document.body.appendChild(script);
    }
  }, [config]);

  // Apple login integration
  useEffect(() => {
    if (!document.getElementById("appleid-signin")) {
      const script = document.createElement("script");
      script.src =
        "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  // Google SDK integration
  useEffect(() => {
    if (!window.google) {
      const script = document.createElement("script");
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.onload = () => setGoogleSdkLoaded(true);
      document.body.appendChild(script);
    } else {
      setGoogleSdkLoaded(true);
    }
  }, []);

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const response = await api.post("/consumer/login", {
        email,
        password,
        clientId: config?.clientId,
      });
      const data = response.data;
      
      if (data.requiresTwoFactor) {
        onAuthSuccess({
          requiresTwoFactor: true,
          consumerId: data.consumerId,
          email: data.email,
          clientId: config?.clientId,
          twoFactorMethod: data.twoFactorMethod,
        });
      } else {
        if (setConsumerToken) {
          setConsumerToken(data.token);
        } else {
          localStorage.setItem("consumerToken", data.token);
        }
        onAuthSuccess({
          isAuthenticated: true,
          consumerId: data.consumerId,
          email: data.email,
          clientId: config?.clientId,
          requiresTwoFactor: false,
          credits: data.credits,
        });
      }
    } catch (err: any) {
      // Handle axios errors
      const errorData = err.response?.data;
      const errorMessage = errorData?.message || err.message || "Login failed";
      
      // Handle incomplete registration - redirect user to complete it
      if (errorData?.incompleteRegistration) {
        const pendingRegData = {
          consumerId: errorData.consumerId,
          email: errorData.email,
          clientId: errorData.clientId,
          step: errorData.requiresPassword ? 'setPassword' : 'twoFactorSetup'
        };
        localStorage.setItem(`pendingRegistration_${config?.clientId}`, JSON.stringify(pendingRegData));
        
        onAuthSuccess({
          isAuthenticated: false,
          consumerId: errorData.consumerId,
          email: errorData.email,
          clientId: errorData.clientId,
          requiresPassword: errorData.requiresPassword,
          requiresTwoFactor: errorData.requiresTwoFactor,
        });
        
        const message = errorData.requiresPassword 
          ? "Please complete registration - set your password."
          : "Please complete registration - set up 2FA.";
        showSnackbar?.(message, "warning");
        
        if (errorData.requiresPassword) {
          onStateChange("setPassword");
        } else {
          onStateChange("twoFactorSetup");
        }
        return;
      }
      
      setError(errorMessage);
      showSnackbar?.(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      {(error || socialError) && <div className="error-message">{error || socialError}</div>}
      {/* Social Login Buttons */}
      <div className="social-login-section">
        <div className="social-buttons">
          <button
            type="button"
            className="social-btn social-btn-apple"
            onClick={handleAppleLogin}
            disabled={loading || !!socialLoading}
          >
            <span className="social-icon social-icon-apple"></span>
            {socialLoading === "apple" ? "Connecting..." : "Sign in with Apple"}
          </button>
          <button
            type="button"
            className="social-btn social-btn-google"
            onClick={handleGoogleLogin}
            disabled={loading || !!socialLoading || !googleSdkLoaded}
          >
            <span className="social-icon social-icon-google"></span>
            {socialLoading === "google"
              ? "Connecting..."
              : !googleSdkLoaded
                ? "Loading Google..."
                : "Sign in with Google"}
          </button>
          {/* <button
            type="button"
            className="social-btn social-btn-facebook"
            onClick={handleFacebookLogin}
            disabled={loading || socialLoading}
          >
            <span className="social-icon social-icon-facebook"></span>
            {socialLoading === "facebook"
              ? "Connecting..."
              : "Continue with Facebook"}
          </button> */}
        </div>
        <span>
          <strong>OR</strong>
        </span>
      </div>
      {/* Email/Password Form */}
      <form onSubmit={handleSubmit} className="email-login-form">
        <TextField
          type="email"
          label="Email"
          name="email"
          value={email}
          onChange={(e: any) => setEmail(e.target.value)}
          fullWidth
          required
          disabled={loading || !!socialLoading}
          inputProps={{ maxLength: 255 }}
          sx={{
            width: "100%",
            maxWidth: 300,
            marginBottom: 3,
          }}
          autoFocus
        />
        <TextField
          type={showPassword ? "text" : "password"}
          label="Password"
          name="password"
          value={password}
          onChange={(e: any) => setPassword(e.target.value)}
          fullWidth
          required
          disabled={loading || !!socialLoading}
          inputProps={{ maxLength: 64 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            width: "100%",
            maxWidth: 300,
            marginBottom: 3,
          }}
        />
        <Button
          type="submit"
          variant="contained"
          size="small"
          sx={{
            maxWidth: 300,
            width: "100%",
            backgroundColor: "var(--color-primary)",
            "&:hover": { backgroundColor: "var(--color-secondary)" },
          }}
          disabled={loading || !!socialLoading}
        >
          Sign In
        </Button>
      </form>
      {/* Register Link */}
      <div className="footer-links">
        <p>
          Don't have an account?{" "}
          <Typography
            component="span"
            variant="body2"
            onClick={() => onStateChange("register")}
            color="var(--color-primary)"
            sx={{ cursor: "pointer" }}
          >
            Register here
          </Typography>
        </p>
        <Typography
          component="p"
          variant="body2"
          onClick={() => onStateChange("forgotPassword")}
          sx={{ cursor: "pointer" }}
        >
          Forgot password?
        </Typography>
      </div>
    </div>
  );
};

export default Login;
